import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { BackButton, StatusBadge } from '../components/common';
import { navigateBack } from '../utils/navigationUtils';
import { useClubPermissions } from '../hooks/useClubPermissions';
import PaymentTracking from '../components/club/PaymentTracking';
import { Heading, Text } from '../components/common/Typography';
import { Card } from '../components/common/Card';

const PaymentTrackingPage = ({ currentUser }) => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search || '');
  const tabParam = searchParams.get('tab');
  const highlightPaymentId = searchParams.get('highlightPayment') || null;
  
  // Use club permissions hook to get club context from URL
  const { 
    club: selectedClub, 
    role: userClubRole, 
    loading: clubLoading, 
    hasPermission, 
    error: clubError 
  } = useClubPermissions(clubId, currentUser);

  const isAdmin =
    currentUser?.role === 'admin' ||
    currentUser?.role === 'super_admin' ||
    userClubRole === 'admin' ||
    userClubRole === 'owner' ||
    userClubRole === 'host';

  // Show loading state while fetching club permissions
  if (clubLoading) {
    return (
      <div className="max-w-app-shell mx-auto px-3 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <Text className="ml-3 text-text-secondary">Loading club details...</Text>
        </div>
      </div>
    );
  }

  // Show error if club not found or no permission
  if (clubError || !hasPermission) {
    return (
      <div className="max-w-app-shell mx-auto px-3 sm:px-6 py-6 sm:py-8">
        <Card variant="default" size="large" className="text-center py-12">
          <div className="text-6xl mb-4">🚫</div>
          <Heading level={2} className="mb-4">Access Denied</Heading>
          <Text className="mb-8 text-text-secondary">
            {clubError || 'You do not have permission to access this club'}
          </Text>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        <Card variant="danger" size="large" className="text-center">
          <Heading level={2} className="text-danger mb-4">Access Denied</Heading>
          <Text className="text-danger-dark">You do not have permission to access payment tracking. Only club hosts, admins, and owners can open this page.</Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-app-shell mx-auto px-3 sm:px-6 py-6 sm:py-8">
      {/* Back Button */}
      <div className="mb-6 sm:mb-8">
        <BackButton 
          onClick={() => {
            navigateBack(navigate, location, clubId, `/club-management/${clubId}`);
          }}
        >
          Back to Management
        </BackButton>
      </div>
      
      {/* Header */}
      <div className="text-center mb-8 sm:mb-12">
        <Heading level={1} className="text-2xl sm:text-display text-text-primary mb-3 sm:mb-4 break-words px-1">
          <span className="inline-flex flex-wrap items-center justify-center gap-2">
            <span>💳 Payment Tracking</span>
            <StatusBadge variant="info" size="small" className="!px-1.5 !py-0.5 text-[10px] font-semibold uppercase tracking-wide">
              [BETA]
            </StatusBadge>
          </span>
        </Heading>
        <Text className="text-sm sm:text-body-large text-text-secondary max-w-2xl mx-auto px-1">
          Managing payments for <span className="font-semibold text-text-primary">{selectedClub?.name || 'Unknown Club'}</span>
        </Text>
        <Text className="text-sm text-text-muted mt-2">
          Track outstanding payments, player balances, and session pricing
        </Text>
      </div>

      {/* Payment Tracking Component */}
      <PaymentTracking 
        clubId={selectedClub?.id}
        currentUser={currentUser}
        initialTab={tabParam === 'ledger' ? 'ledger' : undefined}
        highlightPaymentId={highlightPaymentId}
      />
    </div>
  );
};

export default PaymentTrackingPage;
